import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { aborted, blocked, completed, failed, partial } from 'flare-chat-core/contracts/chat/operation-result';
import type { ChatOperationResult } from 'flare-chat-core/contracts';
import { resolveSourcingOutcome, type SourcingReceipt } from './contracts';
import { useApplicationChat } from '../context';
import { buildSourcingSearchRequest, buildSourcingSearchTimelineItems,
  resolveSourcingResultIds, selectLatestSourcingPayload } from '../../adapters/sourcing/search.js';

const useSourcingOwner = (userId?: string) => {
  const { realApi, viewModel, workspaceLocation } = useApplicationChat();
  const { sourcingAPI } = realApi;
  const sessionId = viewModel.sessionList.activeSessionId;
  const projectId = workspaceLocation.effectiveProjectId;
  const [searchItems, setSearchItems] = useState<ReturnType<typeof buildSourcingSearchTimelineItems>>([]);
  const [result, setResult] = useState<ChatOperationResult<SourcingReceipt> | null>(null);
  const [pending, setPending] = useState(false);
  const scope = useMemo(() => ({ sessionId, sourcingAPI, projectId, userId, active: true,
    operation: null as Promise<ChatOperationResult<SourcingReceipt>> | null,
    request: null as ReturnType<typeof buildSourcingSearchRequest> | null,
    controller: null as AbortController | null }),
    [projectId, sessionId, sourcingAPI, userId]);
  const current = useRef(scope);
  current.current = scope;
  useEffect(() => {
    scope.active = true;
    setSearchItems([]); setResult(null); setPending(false);
    return () => { scope.active = false; scope.controller?.abort(); };
  }, [scope]);
  // Keep temporary results until the canonical timeline contains the same retrieval run.
  useEffect(() => {
    const canonical = selectLatestSourcingPayload(viewModel.timeline.items);
    const temporary = selectLatestSourcingPayload(searchItems);
    const runId = canonical.retrieval_run_id || canonical.run_id;
    if (runId && runId === (temporary.retrieval_run_id || temporary.run_id)) setSearchItems([]);
  }, [searchItems, viewModel.timeline.items]);
  const timelineItems = useMemo(() => [...viewModel.timeline.items, ...searchItems], [searchItems, viewModel.timeline.items]);
  const latestPayload = selectLatestSourcingPayload(timelineItems);
  const hasMore = latestPayload.has_more === true;

  const loadMore = useCallback(async (): Promise<ChatOperationResult<SourcingReceipt>> => {
    const isCurrent = () => scope.active && current.current === scope;
    if (!isCurrent() || !hasMore || typeof sourcingAPI?.search !== 'function') return blocked('sourcing_unavailable');
    if (scope.operation) return scope.operation;
    const request = scope.request ?? buildSourcingSearchRequest({ activeSessionId: sessionId, latestPayload,
      loadedTopK: 8, maxTopK: 100, projectId, userId, resultIds: resolveSourcingResultIds(latestPayload) });
    if (!request.query) return blocked('sourcing_query_missing');
    scope.request = request;
    scope.controller = new AbortController();
    setPending(true);
    const execute = async (): Promise<ChatOperationResult<SourcingReceipt>> => {
      let receipt: SourcingReceipt | undefined;
      try {
        const response = await sourcingAPI.search(request, { signal: scope.controller?.signal });
        const resolution = resolveSourcingOutcome(response);
        const acceptedReceipt: SourcingReceipt = { response, persisted: resolution.persisted, acknowledgment: resolution.acknowledgment };
        receipt = acceptedReceipt;
        if (!isCurrent()) return aborted(receipt);
        // Only a backend-accepted projection replaces the last accepted result.
        if (resolution.persisted) {
          setSearchItems(buildSourcingSearchTimelineItems(response));
          scope.request = null;
        }
        const outcomes = {
          completed: () => completed(acceptedReceipt),
          partial: () => partial(acceptedReceipt, new Error(String(response.reason || 'Canonical sourcing commit unconfirmed')), 'sourcingSearch'),
          blocked: () => blocked(String(response.reason || 'sourcing_blocked')),
          failed: () => failed(new Error(String(response.reason || 'sourcing_failed')), 'sourcingSearch'),
        };
        const next = outcomes[resolution.outcome]();
        setResult(next);
        return next;
      } catch (error) {
        const next = receipt ? partial(receipt, error, 'sourcingSearch') : failed(error, 'sourcingSearch');
        if (isCurrent()) setResult(next);
        return isCurrent() ? next : aborted(receipt);
      } finally {
        scope.operation = null;
        if (isCurrent()) setPending(false);
      }
    };
    scope.operation = execute();
    return scope.operation;
  }, [hasMore, latestPayload, projectId, scope, sessionId, sourcingAPI, userId]);
  return { timelineItems, hasMore, pending, result, loadMore };
};
const SourcingContext = createContext<ReturnType<typeof useSourcingOwner> | null>(null);
export const SourcingProvider = ({ userId, children }: { userId?: string; children?: ReactNode }) => {
  const value = useSourcingOwner(userId);
  return <SourcingContext.Provider value={value}>{children}</SourcingContext.Provider>;
};
export const useSourcing = () => {
  const value = useContext(SourcingContext);
  if (!value) throw new Error('Sourcing requires the application provider');
  return value;
};
